import { openDatabase, STORE } from '@/lib/db';
import {
  DEFAULT_WORKSPACE_SETTINGS,
  type Chat,
  type Message,
  type Project,
  type WorkspaceSettings,
} from './types';
import {
  openChat,
  openChats,
  openMessage,
  openMessages,
  openProject,
  openProjects,
  openSettings,
  sealChat,
  sealMessage,
  sealProject,
  sealSettings,
} from './seal';

const SETTINGS_KEY = 'workspace';

// ─── In-memory fallback when IDB is unavailable ───

const mem = {
  settings: { ...DEFAULT_WORKSPACE_SETTINGS } as WorkspaceSettings,
  projects: new Map<string, Project>(),
  chats: new Map<string, Chat>(),
  messages: new Map<string, Message>(),
};

// ─── Settings ───

export async function getSettings(): Promise<WorkspaceSettings> {
  const db = await openDatabase();
  if (!db) {
    return openSettings({
      ...DEFAULT_WORKSPACE_SETTINGS,
      ...mem.settings,
      activeChatByProject: { ...mem.settings.activeChatByProject },
    });
  }

  const stored = await db.get(STORE.SETTINGS, SETTINGS_KEY);
  if (!stored || typeof stored !== 'object') {
    return { ...DEFAULT_WORKSPACE_SETTINGS };
  }
  const s = stored as WorkspaceSettings;
  return openSettings({
    ...DEFAULT_WORKSPACE_SETTINGS,
    ...s,
    encryptLocal: s.encryptLocal ?? true,
    activeChatByProject: s.activeChatByProject ?? {},
    globalInstructions: s.globalInstructions ?? '',
  });
}

export async function saveSettings(settings: WorkspaceSettings): Promise<void> {
  const sealed = await sealSettings(settings);
  const db = await openDatabase();
  if (!db) {
    mem.settings = sealed;
    return;
  }
  await db.put(STORE.SETTINGS, sealed, SETTINGS_KEY);
}

// ─── Projects ───

export async function listProjects(): Promise<Project[]> {
  const db = await openDatabase();
  if (!db) {
    const list = [...mem.projects.values()]
      .filter((p) => !p.archived)
      .sort((a, b) => b.updatedAt - a.updatedAt);
    return openProjects(list);
  }
  const all = await db.getAll(STORE.PROJECTS);
  const filtered = all.filter((p) => !p.archived).sort((a, b) => b.updatedAt - a.updatedAt);
  return openProjects(filtered);
}

export async function getProject(id: string): Promise<Project | undefined> {
  const db = await openDatabase();
  const raw = db ? await db.get(STORE.PROJECTS, id) : mem.projects.get(id);
  if (!raw) return undefined;
  return openProject(raw);
}

export async function putProject(project: Project): Promise<void> {
  const sealed = await sealProject(project);
  const db = await openDatabase();
  if (!db) {
    mem.projects.set(project.id, sealed);
    return;
  }
  await db.put(STORE.PROJECTS, sealed);
}

export async function deleteProject(id: string): Promise<void> {
  const db = await openDatabase();
  const chats = await listChats(id);
  for (const chat of chats) {
    await deleteChat(chat.id);
  }
  if (!db) {
    mem.projects.delete(id);
    return;
  }
  await db.delete(STORE.PROJECTS, id);
}

// ─── Chats ───

export async function listChats(projectId: string): Promise<Chat[]> {
  const db = await openDatabase();
  if (!db) {
    const list = [...mem.chats.values()]
      .filter((c) => c.projectId === projectId)
      .sort((a, b) => b.updatedAt - a.updatedAt);
    return openChats(list);
  }
  const all = await db.getAllFromIndex(STORE.CHATS, 'by-project', projectId);
  return openChats(all.sort((a, b) => b.updatedAt - a.updatedAt));
}

/** Freestanding chats (no project). */
export async function listStandaloneChats(): Promise<Chat[]> {
  const all = await getAllChats();
  return all
    .filter((c) => c.projectId == null || c.projectId === '')
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function getChat(id: string): Promise<Chat | undefined> {
  const db = await openDatabase();
  const raw = db ? await db.get(STORE.CHATS, id) : mem.chats.get(id);
  if (!raw) return undefined;
  return openChat(raw);
}

export async function putChat(chat: Chat): Promise<void> {
  // Normalize freestanding: null projectId (index still accepts it)
  const normalized: Chat = {
    ...chat,
    projectId:
      chat.projectId == null || chat.projectId === ''
        ? null
        : chat.projectId,
  };
  const sealed = await sealChat(normalized);
  const db = await openDatabase();
  if (!db) {
    mem.chats.set(normalized.id, sealed);
    return;
  }
  await db.put(STORE.CHATS, sealed);
}

export async function deleteChat(id: string): Promise<void> {
  await deleteMessagesForChat(id);
  const db = await openDatabase();
  if (!db) {
    mem.chats.delete(id);
    return;
  }
  await db.delete(STORE.CHATS, id);
}

// ─── Messages ───

export async function listMessages(chatId: string): Promise<Message[]> {
  const db = await openDatabase();
  if (!db) {
    const list = [...mem.messages.values()]
      .filter((m) => m.chatId === chatId)
      .sort((a, b) => a.createdAt - b.createdAt);
    return openMessages(list);
  }
  const all = await db.getAllFromIndex(STORE.MESSAGES, 'by-chat', chatId);
  return openMessages(all.sort((a, b) => a.createdAt - b.createdAt));
}

export async function putMessage(message: Message): Promise<void> {
  const sealed = await sealMessage(message);
  const db = await openDatabase();
  if (!db) {
    mem.messages.set(message.id, sealed);
    return;
  }
  await db.put(STORE.MESSAGES, sealed);
}

export async function putMessages(messages: Message[]): Promise<void> {
  const sealed = await Promise.all(messages.map(sealMessage));
  const db = await openDatabase();
  if (!db) {
    for (const m of sealed) mem.messages.set(m.id, m);
    return;
  }
  const tx = db.transaction(STORE.MESSAGES, 'readwrite');
  await Promise.all([...sealed.map((m) => tx.store.put(m)), tx.done]);
}

export async function deleteMessage(id: string): Promise<void> {
  const db = await openDatabase();
  if (!db) {
    mem.messages.delete(id);
    return;
  }
  await db.delete(STORE.MESSAGES, id);
}

export async function deleteMessagesForChat(chatId: string): Promise<void> {
  const db = await openDatabase();
  if (!db) {
    for (const [id, m] of mem.messages) {
      if (m.chatId === chatId) mem.messages.delete(id);
    }
    return;
  }
  const existing = await db.getAllFromIndex(STORE.MESSAGES, 'by-chat', chatId);
  const tx = db.transaction(STORE.MESSAGES, 'readwrite');
  await Promise.all([...existing.map((m) => tx.store.delete(m.id)), tx.done]);
}

// ─── Bulk (export / import) — always plaintext after open ───

export async function getAllProjects(): Promise<Project[]> {
  const db = await openDatabase();
  const raw = db ? await db.getAll(STORE.PROJECTS) : [...mem.projects.values()];
  return openProjects(raw);
}

export async function getAllChats(): Promise<Chat[]> {
  const db = await openDatabase();
  const raw = db ? await db.getAll(STORE.CHATS) : [...mem.chats.values()];
  return openChats(raw);
}

export async function getAllMessages(): Promise<Message[]> {
  const db = await openDatabase();
  const raw = db ? await db.getAll(STORE.MESSAGES) : [...mem.messages.values()];
  return openMessages(raw);
}

/** Raw sealed rows for migration (do not open). */
export async function getAllProjectsRaw(): Promise<Project[]> {
  const db = await openDatabase();
  if (!db) return [...mem.projects.values()];
  return db.getAll(STORE.PROJECTS);
}

export async function getAllChatsRaw(): Promise<Chat[]> {
  const db = await openDatabase();
  if (!db) return [...mem.chats.values()];
  return db.getAll(STORE.CHATS);
}

export async function getAllMessagesRaw(): Promise<Message[]> {
  const db = await openDatabase();
  if (!db) return [...mem.messages.values()];
  return db.getAll(STORE.MESSAGES);
}

export async function replaceWorkspace(data: {
  settings?: WorkspaceSettings;
  projects: Project[];
  chats: Chat[];
  messages: Message[];
}): Promise<void> {
  // Seal everything on write — including settings (global instructions)
  const projects = await Promise.all(data.projects.map(sealProject));
  const chats = await Promise.all(data.chats.map(sealChat));
  const messages = await Promise.all(data.messages.map(sealMessage));
  const settings = data.settings ? await sealSettings(data.settings) : undefined;

  const db = await openDatabase();
  if (!db) {
    mem.projects.clear();
    mem.chats.clear();
    mem.messages.clear();
    for (const p of projects) mem.projects.set(p.id, p);
    for (const c of chats) mem.chats.set(c.id, c);
    for (const m of messages) mem.messages.set(m.id, m);
    if (settings) mem.settings = settings;
    return;
  }

  const tx = db.transaction(
    [STORE.SETTINGS, STORE.PROJECTS, STORE.CHATS, STORE.MESSAGES],
    'readwrite',
  );

  await tx.objectStore(STORE.PROJECTS).clear();
  await tx.objectStore(STORE.CHATS).clear();
  await tx.objectStore(STORE.MESSAGES).clear();

  for (const p of projects) await tx.objectStore(STORE.PROJECTS).put(p);
  for (const c of chats) await tx.objectStore(STORE.CHATS).put(c);
  for (const m of messages) await tx.objectStore(STORE.MESSAGES).put(m);
  if (settings) {
    await tx.objectStore(STORE.SETTINGS).put(settings, SETTINGS_KEY);
  }
  await tx.done;
}

export async function mergeWorkspace(data: {
  projects: Project[];
  chats: Chat[];
  messages: Message[];
}): Promise<void> {
  const projects = await Promise.all(data.projects.map(sealProject));
  const chats = await Promise.all(data.chats.map(sealChat));
  const messages = await Promise.all(data.messages.map(sealMessage));

  const db = await openDatabase();
  if (!db) {
    for (const p of projects) mem.projects.set(p.id, p);
    for (const c of chats) mem.chats.set(c.id, c);
    for (const m of messages) mem.messages.set(m.id, m);
    return;
  }
  const tx = db.transaction(
    [STORE.PROJECTS, STORE.CHATS, STORE.MESSAGES],
    'readwrite',
  );
  for (const p of projects) await tx.objectStore(STORE.PROJECTS).put(p);
  for (const c of chats) await tx.objectStore(STORE.CHATS).put(c);
  for (const m of messages) await tx.objectStore(STORE.MESSAGES).put(m);
  await tx.done;
}

/**
 * Re-encrypt all stored rows with the current session vault key.
 * Call after enabling encryption or rotating unlock.
 */
export async function reencryptAllLocalData(): Promise<void> {
  const projects = await getAllProjects(); // opened
  const chats = await getAllChats();
  const messages = await getAllMessages();
  const settings = await getSettings();
  for (const p of projects) await putProject(p);
  for (const c of chats) await putChat(c);
  for (const m of messages) await putMessage(m);
  await saveSettings(settings);
}
